import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, MessageCircle } from "lucide-react";
import { apiFetch } from "./api/client";
import { UI } from "./designSystem";

const ADMIN_PATH = "/admin";

const STATUS_LABEL = {
  unknown: "Aguardando contato",
  active: "Ativo",
  opted_out: "Pediu para sair",
  no_whatsapp: "Sem WhatsApp",
  invalid: "Número inválido"
};
const STATUS_COLOR = {
  unknown: "#CBD5E1",
  active: "#86EFAC",
  opted_out: "#FCA5A5",
  no_whatsapp: "#FDE68A",
  invalid: "#FCA5A5"
};

function fmtPhone(p) {
  const d = String(p || "").replace(/\D/g, "");
  const m = d.match(/^55(\d{2})(\d{5})(\d{4})$/);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : p || "-";
}
function fmtDate(v) {
  if (!v) return "-";
  return new Date(v).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function AdminAgente() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await apiFetch("/admin/agent/contatos"));
    } catch (e) {
      setError(e?.message || "Não foi possível carregar.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data?.contatos || []).filter((c) => {
      if (filter !== "all" && c.status !== filter) return false;
      if (!term) return true;
      return `${c.nome || ""} ${c.telefone || ""}`.toLowerCase().includes(term);
    });
  }, [data, filter, q]);

  const card = { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: 14 };

  return (
    <div style={{ minHeight: "100vh", width: "100%", boxSizing: "border-box", backgroundColor: UI.shell, color: UI.text, fontFamily: "'DM Sans', system-ui, sans-serif", padding: "24px 16px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <Link to={ADMIN_PATH} style={{ color: UI.text, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none", opacity: 0.8 }}>
          <ArrowLeft size={16} /> Painel
        </Link>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0" }}>
          <h1 style={{ margin: 0, fontSize: 24, display: "flex", alignItems: "center", gap: 8 }}>
            <MessageCircle size={22} /> Agente WhatsApp (Barnabé)
          </h1>
          <button onClick={load} style={{ ...card, color: UI.text, cursor: "pointer", display: "inline-flex", gap: 6, alignItems: "center", padding: "8px 12px" }}>
            <RefreshCw size={14} /> Atualizar
          </button>
        </div>

        {error && <div style={{ ...card, color: "#FCA5A5" }}>{error}</div>}
        {loading && !data && <div style={{ opacity: 0.7 }}>Carregando...</div>}

        {data && (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              <button onClick={() => setFilter("all")} style={{ ...card, padding: "8px 12px", cursor: "pointer", color: UI.text, outline: filter === "all" ? "2px solid #5EEAD4" : "none" }}>
                Todos <b>{data.total}</b>
              </button>
              {Object.entries(data.resumo).map(([s, n]) => (
                <button key={s} onClick={() => setFilter(s)} style={{ ...card, padding: "8px 12px", cursor: "pointer", color: STATUS_COLOR[s] || UI.text, outline: filter === s ? "2px solid #5EEAD4" : "none" }}>
                  {STATUS_LABEL[s] || s} <b>{n}</b>
                </button>
              ))}
            </div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou telefone"
              style={{ width: "100%", boxSizing: "border-box", padding: 10, borderRadius: 10, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.06)", color: UI.text, marginBottom: 12 }} />

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: "left", opacity: 0.7 }}>
                    {["Nome", "Telefone", "Tipo", "Situação", "Consentimento", "Envios", "Último envio", "Última resposta"].map((h) => (
                      <th key={h} style={{ padding: "8px 6px", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.personId} style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                      <td style={{ padding: "8px 6px" }}>{c.nome || "(apagado)"}</td>
                      <td style={{ padding: "8px 6px", whiteSpace: "nowrap" }}>{fmtPhone(c.telefone)}</td>
                      <td style={{ padding: "8px 6px" }}>{c.tipo}{c.origem ? ` · ${c.origem}` : ""}</td>
                      <td style={{ padding: "8px 6px", color: STATUS_COLOR[c.status] || UI.text }}>
                        {STATUS_LABEL[c.status] || c.status}{c.motivo ? ` (${c.motivo})` : ""}
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        {c.revogouTudo ? "Revogou" : c.consentimento.length ? c.consentimento.join(", ") : c.convitePermitido ? "Convite (leve)" : "Nenhum"}
                      </td>
                      <td style={{ padding: "8px 6px" }}>{c.totalEnvios}</td>
                      <td style={{ padding: "8px 6px" }}>
                        {c.ultimoEnvio ? `${fmtDate(c.ultimoEnvio.em)} · ${c.ultimoEnvio.tipo || ""} · ${c.ultimoEnvio.status}` : "-"}
                      </td>
                      <td style={{ padding: "8px 6px" }}>{fmtDate(c.ultimaMensagemRecebida)}</td>
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr><td colSpan={8} style={{ padding: 16, opacity: 0.6 }}>Nenhum contato ainda. Eles aparecem aqui depois da importação.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
